/* eslint-disable @ngrx/no-dispatch-in-effects */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CompaniesSearchConnectorService } from '@warema/shared/connector-services';
import { SnackbarService } from '@warema/shared/helpers';
import { Company } from '@warema/shared/models';
import { TenantConfigurationService } from '@warema/shared/tenant-config';
import { EMPTY } from 'rxjs';
import { catchError, exhaustMap, map, withLatestFrom } from 'rxjs/operators';
import * as SearchActions from './search.actions';
import { SearchState } from './search.reducers';
import * as SearchSelectors from './search.selectors';

@Injectable({
    providedIn: 'root',
})
export class SearchEffects {
    private readonly _actions$ = inject(Actions);
    private readonly _companiesSearchConnectorService = inject(CompaniesSearchConnectorService);
    private readonly _store = inject<Store<SearchState>>(Store);
    private readonly _tenantConfig = inject(TenantConfigurationService);
    private readonly _snackbar = inject(SnackbarService);

    public searchCompanies$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(SearchActions.SearchCompanies),
            exhaustMap(action =>
                this._companiesSearchConnectorService.search(action.search).pipe(
                    map(data => {
                        const companies = data.companies as Array<Company>;
                        this._store.dispatch(
                            SearchActions.noSearchResultsChanged({ noSearchResults: companies.length === 0 }),
                        );
                        this._store.dispatch(
                            SearchActions.canSearchMoreChanged({
                                canSearchMore: data.companies.length === action.search.count,
                            }),
                        );
                        this._store.dispatch(SearchActions.isSearchRunningStatusChanged({ isSearchRunning: false }));
                        return SearchActions.CompaniesSearched({ companies });
                    }),
                    catchError(() => {
                        this._store.dispatch(SearchActions.isSearchRunningStatusChanged({ isSearchRunning: false }));
                        void this._snackbar.showGenericErrorSnackbar();
                        return EMPTY;
                    }),
                ),
            ),
        );
    });

    public searchMoreCompanies$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(SearchActions.SearchMoreCompanies),
            withLatestFrom(this._store.select(SearchSelectors.selectAllCompanies)),
            exhaustMap(([action, companies]) =>
                this._companiesSearchConnectorService.searchMore(action.search).pipe(
                    map(data => {
                        const allCompanies = [...companies, ...data.companies];
                        const companySearchConfig = this._tenantConfig.settings.companySearch;

                        this._store.dispatch(
                            SearchActions.canSearchMoreChanged({
                                canSearchMore:
                                    allCompanies.length < companySearchConfig.maxSearchResultLength &&
                                    action.search.range < companySearchConfig.maxSearchRadius &&
                                    data.companies.length === action.search.count,
                            }),
                        );

                        this._store.dispatch(SearchActions.isSearchRunningStatusChanged({ isSearchRunning: false }));

                        return SearchActions.MoreCompaniesSearched({ companies: data.companies as Array<Company> });
                    }),
                    catchError(() => {
                        this._store.dispatch(SearchActions.isSearchRunningStatusChanged({ isSearchRunning: false }));
                        void this._snackbar.showGenericErrorSnackbar();
                        return EMPTY;
                    }),
                ),
            ),
        );
    });
}
